import Box from "@mui/material/Box";
import { ThemeProvider } from "@mui/material/styles";
import React from "react";
import { HeadProvider, Meta, Title } from "react-head";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import AbstractPage from "./components/AbstractPage";
import HomepageComponent from "./components/HomepageComponent";
import PlayPage from "./components/PlayPage";
import { SeasonProvider } from "./components/SeasonContext";
import { AuthProvider } from "./context/AuthContext";
import AdminLoginPage from "./sections/AdminLoginPage";
import AdminStatisticsView from "./sections/AdminStatisticsView";
import HistoriePage from "./sections/HistoriePage";
import ImpressumPage from "./sections/ImpressumPage";
import PrivacyPolicyPage from "./sections/PrivacyPolicyPage";
import SeasonTablePage from "./sections/SeasonTablePage";
import SpielplanPage from "./sections/SpielplanPlage";
import TeamDetailPage from "./sections/TeamDetailPage";
import TeamListPageWithProvider from "./sections/TeamListPage";
import { appTheme } from "./theme/siteTheme";

// Route path constants
const ROUTE_HISTORY = "/history";
const ROUTE_IMPRESSUM = "/impressum";
const ROUTE_PRIVACY = "/privacy-policy";
const ROUTE_ROOT = "/";
const ROUTE_SCHEDULE = "/spielplan";
const ROUTE_PLAY = "/play";

const NAV_LINKS = [
  { href: "/#about", label: "Über uns" },
  { href: "/#lead", label: "Ligaleitung" },
  { href: "/teams", label: "Mannschaften" },
  { href: "/table", label: "Tabelle" },
  { href: `${ROUTE_SCHEDULE}?displayOption=spieltag`, label: "Spielplan" },
  { href: ROUTE_HISTORY, label: "Historie & Dokumente" },
  // { href: "/play", label: "Spielen" },
];

const LEGAL_LINKS = [
  { href: ROUTE_IMPRESSUM, label: "Impressum" },
  { href: ROUTE_PRIVACY, label: "Datenschutz" },
];

function App() {
  return (
    <HeadProvider>
      <AuthProvider>
        <ThemeProvider theme={appTheme}>
          <SeasonProvider>
            <Title>Dartliga Württemberg | Steeldart Liga Dartsport</Title>
            <Meta
              name="description"
              content="Offizielle Webseite der Dartliga Württemberg. Steeldart, Darts, Dartsport, Teams, Tabelle, Spielplan und mehr."
            />
            <Meta
              name="keywords"
              content="Dart, Württemberg, Liga, Steeldart, Darts, Dartsport, Teams, Tabelle, Spielplan"
            />
            <Meta property="og:title" content="Dartliga Württemberg" />
            <Meta
              property="og:description"
              content="Alles rund um die Steeldart Liga in Württemberg: Teams, Tabelle, Historie, Dartsport."
            />
            <Meta property="og:type" content="website" />
            <Meta property="og:locale" content="de_DE" />
            <Router>
              <AppRoutes />
            </Router>
          </SeasonProvider>
        </ThemeProvider>
      </AuthProvider>
    </HeadProvider>
  );
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const handleNavClick = (e: Event) => {
      const target = e.target as HTMLAnchorElement;
      const href = target.getAttribute("href");

      if (href?.startsWith("/#")) {
        const sectionId = href.slice(2); // skip '/#'
        const isRoot = window.location.pathname === ROUTE_ROOT;
        if (!isRoot) {
          e.preventDefault();
          navigate("/#" + sectionId);
        } else {
          const section = document.getElementById(sectionId);
          if (section) {
            e.preventDefault();
            section.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      } else if (href?.includes("?displayOption=")) {
        // Handle dropdown navigation with query parameters
        e.preventDefault();
        navigate(href);
      }
    };
    document.addEventListener("click", handleNavClick);
    return () => document.removeEventListener("click", handleNavClick);
  }, [navigate]);

  // Scroll to anchor on route change
  React.useEffect(() => {
    if (window.location.pathname === ROUTE_ROOT) {
      const hash = window.location.hash;
      if (hash && hash.startsWith("#")) {
        setTimeout(() => {
          const section = document.getElementById(hash.slice(1));
          if (section) {
            section.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 0);
      }
    }
  }, []);

  return (
    <div>
      <Routes location={location}>
        <Route
          path={ROUTE_HISTORY}
          element={
            <AbstractPage navLinks={NAV_LINKS} legalLinks={LEGAL_LINKS}>
              <HistoriePage />
            </AbstractPage>
          }
        />
        <Route
          path={ROUTE_IMPRESSUM}
          element={
            <AbstractPage navLinks={NAV_LINKS} legalLinks={LEGAL_LINKS}>
              <ImpressumPage />
            </AbstractPage>
          }
        />
        <Route
          path={ROUTE_PRIVACY}
          element={
            <AbstractPage navLinks={NAV_LINKS} legalLinks={LEGAL_LINKS}>
              <PrivacyPolicyPage />
            </AbstractPage>
          }
        />
        <Route
          path="/teams/:id"
          element={
            <AbstractPage navLinks={NAV_LINKS} legalLinks={LEGAL_LINKS}>
              <TeamDetailPage />
            </AbstractPage>
          }
        />
        <Route
          path="/teams"
          element={
            <AbstractPage navLinks={NAV_LINKS} legalLinks={LEGAL_LINKS}>
              <TeamListPageWithProvider />
            </AbstractPage>
          }
        />
        <Route
          path={ROUTE_SCHEDULE}
          element={
            <AbstractPage navLinks={NAV_LINKS} legalLinks={LEGAL_LINKS}>
              <SpielplanPage />
            </AbstractPage>
          }
        />
        <Route
          path="table"
          element={
            <AbstractPage navLinks={NAV_LINKS} legalLinks={LEGAL_LINKS}>
              <SeasonTablePage />
            </AbstractPage>
          }
        />
        <Route
          path={ROUTE_ROOT}
          element={
            <AbstractPage navLinks={NAV_LINKS} legalLinks={LEGAL_LINKS}>
              <HomepageComponent />
            </AbstractPage>
          }
        />

        <Route
          path={ROUTE_PLAY}
          element={
            <AbstractPage navLinks={NAV_LINKS} legalLinks={LEGAL_LINKS}>
              <PlayPage />
            </AbstractPage>
          }
        />

        <Route
          path="/management"
          element={
            <AbstractPage navLinks={NAV_LINKS} legalLinks={LEGAL_LINKS}>
              <Box component="main">
                <AdminLoginPage />
              </Box>
            </AbstractPage>
          }
        />
        <Route
          path="/statistics"
          element={
            <AbstractPage navLinks={NAV_LINKS} legalLinks={LEGAL_LINKS}>
              {/* <Box component="main"> */}
              <AdminStatisticsView />
              {/* </Box> */}
            </AbstractPage>
          }
        />
      </Routes>
    </div>
  );
}

export default App;
